<template>
  <header
    :class="{ 'is-mobile': isMobile }"
    class="the-user"
  >
    <div class="the-user__left">
      <template v-if="isMobile">
        <button
          ref="menuButton"
          type="button"
          class="menuOpenBtn"
          aria-label="Menu principal"
          :aria-expanded="String(menuOpen)"
          aria-controls="navigation-menu"
          @click="$emit('toggle')"
          @keydown.esc="menuOpen && $emit('toggle')"
        >
          <i
            class="fas"
            :class="menuOpen ? 'fa-times' : 'fa-bars'"
            aria-hidden="true"
          />
        </button>
        <TheLogo
          wide
          class="the-user__logo"
        />
      </template>
    </div>
    <div class="the-user__right">
      <LiveloPoints
        v-if="rewardsVisible"
        class="livelo-points"
      />

      <TheNotifications v-if="user.name" />

      <button
        ref="accountButton"
        type="button"
        class="the-user__account"
        :aria-label="accountLabel"
        :aria-expanded="String(open)"
        aria-controls="user-account-menu"
        @click="open = !open"
      >
        <span
          v-if="!isMobile"
          :class="{ 'no-user': !user.name }"
          class="greetings"
          :title="user.name"
        >
          {{ welcomeMessage }}
        </span>
        <AccountAvatar
          :icon="icon"
          class="the-user__avatar"
        />
      </button>

      <div
        v-if="open"
        ref="menu"
        class="menu"
      >
        <UserAccountMenu
          id="user-account-menu"
          @close="closeAndFocus"
          @accounts="$emit('accounts')"
        />
      </div>
    </div>
  </header>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

import { accountButtonLabel, showsRewards } from '@/utils/userAccount';
import roleManagerMixin from '@/mixins/roleManager.mixin';
import AccountAvatar from '@/components/AccountAvatar.vue';
import LiveloPoints from '@/components/LiveloPoints.vue';
import TheLogo from '@/components/TheLogo.vue';
import TheNotifications from '@/components/TheNotifications.vue';
import UserAccountMenu from '@/components/UserAccountMenu.vue';

export default {
  name: 'TheUser',
  components: {
    AccountAvatar,
    LiveloPoints,
    TheLogo,
    TheNotifications,
    UserAccountMenu,
  },
  mixins: [roleManagerMixin],
  props: {
    menuOpen: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    open: false,
  }),
  computed: {
    ...mapState({
      user: (state) => state.user.userInfo,
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
      isInternalUser: 'user/isInternalUser',
    }),
    rewardsVisible() {
      return showsRewards({
        user: this.user,
        isInternalUser: this.isInternalUser,
        isCollector: this.isCollector,
      });
    },
    icon() {
      if (!this.user.name) return 'fa-exclamation';

      return this.open ? 'fa-times' : undefined;
    },
    accountLabel() {
      return accountButtonLabel(this.user.name);
    },
    welcomeMessage() {
      if (this.user.name) return this.user.name;
      return 'no user';
    },
  },
  watch: {
    open(value) {
      if (value) {
        window.addEventListener('click', this.clickedOutside, true);
        window.addEventListener('keydown', this.escape, true);
      } else {
        window.removeEventListener('click', this.clickedOutside, true);
        window.removeEventListener('keydown', this.escape, true);
      }
    },
  },
  beforeDestroy() {
    window.removeEventListener('click', this.clickedOutside, true);
    window.removeEventListener('keydown', this.escape, true);
  },
  methods: {
    clickedOutside({ target }) {
      if (target.closest('.menu') || this.$refs.accountButton?.contains(target)) return;

      this.close();
    },
    escape(event) {
      if (event.key !== 'Escape') return;

      event.preventDefault();
      this.close();
      this.$refs.accountButton?.focus();
    },
    close() {
      this.open = false;
    },
    closeAndFocus(refocus = true) {
      this.close();

      if (refocus) this.$nextTick(() => this.$refs.accountButton?.focus());
    },
    focusMenuButton() {
      this.$refs.menuButton?.focus();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';

.the-user {
  position: relative;

  display: flex;
  padding: 1rem;
  background-color: $color-primary;

  justify-content: space-between;
  align-items: center;

  &.is-mobile {
    padding: 0 0.5rem;

    :deep(.notif-bell) {
      min-width: $target-touch;
      min-height: $target-touch;
    }

    :deep(.livelo-notification) {
      border-block: 0.375rem solid transparent;
      min-height: $target-touch;

      background-clip: padding-box;
    }

    .menuOpenBtn {
      @include ghost-control;

      display: flex;
      width: $target-touch;
      height: $target-touch;
      flex: none;

      align-items: center;
      justify-content: center;
      font-size: 1rem;
    }

    .the-user__account {
      width: $target-touch;
      height: $target-touch;
      padding: 0;

      justify-content: center;
    }

    .the-user__avatar {
      --avatar-size: 30px;
      --avatar-icon: 0.725rem;
    }
  }

  > div {
    display: flex;
    align-items: center;
  }

  &__left {
    gap: .5rem;
    // Lets the read-only tag ellipsize instead of pushing the header wider.
    min-width: 0;
  }

  &__right {
    gap: .5rem;

    @media (min-width: $breakpoint-desktop) {
      gap: 1rem;
    }
  }
}

.the-user__logo {
  height: 100%;
  background: none;

  :deep(img) {
    width: auto;
    max-width: 6rem;
    height: 1.5rem;
    padding: 0;
  }
}

.the-user__account {
  @include ghost-control;

  display: inline-flex;
  height: $target-pointer;
  padding: 0 0 0 0.5rem;
  gap: 0.5rem;

  align-items: center;
}

.greetings {
  overflow: hidden;
  max-width: 16rem;

  font-size: 1rem;
  line-height: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.livelo-points {
  @media (min-width: $breakpoint-desktop) {
    margin-right: 1rem;
  }
}

.no-user {
  &.greetings {
    font: {
      style: italic;
      weight: 400;
    }
  }
}

.menu {
  @include floating-surface;

  position: absolute;
  top: calc(100% - 7.5px);
  right: 15px;
  z-index: 999;

  overflow: hidden;
  width: 330px;
  max-width: calc(100vw - 2rem);
}
</style>
